/**
 * Epic 278 Slice C/D — Case / Scan / Records register row models.
 * Customer-facing Scan/case/record labels come from Slice D shared resolver
 * (`workspaceResolvedState` / server `customer_*` fields). Do not invent states.
 */

import {
  CONTROL_STAGES,
  mapControlStage,
  mapScanCoverageLabel,
  mapScanResultLabel,
  mapScanWorkflowState,
  type ControlStage,
  type RecordState as ResolvedRecordState,
  type ScanWorkflowState,
} from './workspaceResolvedState';
import {
  OPEN_SAFETY_CASE_LABEL,
  REVIEW_SAFETY_SCAN_LABEL,
  mapBuyerCaseState,
} from './managedSafetyBuyerUx';
import type { BuyerCaseState } from './managedSafetyBuyerUx';
import { START_MONITORING_HELPER } from './workspaceCaseOps';
import {
  managedCaseLabel,
  REVIEW_AND_SIGN_LABEL,
  OPEN_SCAN_LABEL,
  VIEW_RECORD_LABEL,
  VIEW_SAFETY_SCAN_LABEL,
} from './safetyCasesWorkspacePresentationCopy';
import {
  buyerConditionDisplayName,
  buyerOwnerDisplayName,
  buyerScanDefinitionDisplayName,
  formatPersistedScanResult,
  mapBuyerCaseStatusLabel,
} from '../buyerFacing/conditionDisplayNames';
import {
  buyerScanActionPresentation,
  dedupeBuyerLocationLabel,
  formatBuyerScanResultHierarchy,
} from './scanBuyerResultHierarchy';
import { NO_CURRENT_SCAN_LABEL } from './scheduledRunScanOutcome';
import { formatCameraLevelScope } from './cameraScopeDisplay';
import type { HazardClassKey } from '../../pages/acceptanceProof/hazardClassReportOptions';
import { V1_HAZARD_CLASS_OPTIONS, isHazardClassKey } from '../../pages/acceptanceProof/hazardClassReportOptions';
import { resolveIncidentHazardClassKey } from '../incidents/incidentHazardClass';

export { START_MONITORING_HELPER };

export const REGISTER_PAGE_SIZE = 25;
export const DASHBOARD_FETCH_PAGE = 50;
export const DASHBOARD_FETCH_MAX = 100;

export { CONTROL_STAGES, mapControlStage, mapScanCoverageLabel, mapScanResultLabel, mapScanWorkflowState };
export type { ControlStage, ScanWorkflowState };

export type ScanRegisterTriggerLabel = 'Scheduled' | 'Manual' | 'Follow-up';

/** Map persisted scan trigger_type to register label; null/unknown → omit (display —). */
export function mapScanRegisterTrigger(
  triggerType?: string | null,
): ScanRegisterTriggerLabel | null {
  const normalized = String(triggerType || '').trim().toUpperCase();
  if (normalized === 'SCHEDULED') return 'Scheduled';
  if (normalized === 'MANUAL') return 'Manual';
  if (normalized === 'FOLLOW_UP' || normalized === 'FOLLOW-UP' || normalized === 'FOLLOWUP') {
    return 'Follow-up';
  }
  return null;
}

type RegisterRevisionSource = {
  revision?: number | string | null;
  revisions?: Array<{ revision?: number | null }> | null;
};

/** Coerce persisted revision metadata to a positive integer; never invent. */
export function coerceRegisterRevision(source: RegisterRevisionSource): number | null {
  const direct = source.revision;
  if (typeof direct === 'number' && direct >= 1) return Math.floor(direct);
  if (typeof direct === 'string' && direct.trim()) {
    const parsed = Number(direct.trim());
    if (Number.isFinite(parsed) && parsed >= 1) return Math.floor(parsed);
    const match = direct.trim().match(/(\d+)/);
    if (match) {
      const fromLabel = Number(match[1]);
      if (Number.isFinite(fromLabel) && fromLabel >= 1) return Math.floor(fromLabel);
    }
  }
  const revisionValues = (source.revisions || [])
    .map((entry) => (typeof entry?.revision === 'number' ? entry.revision : null))
    .filter((value): value is number => value != null && value >= 1);
  if (revisionValues.length === 0) return null;
  return Math.max(...revisionValues);
}

/** Records register primary list: signed + superseded only. */
export type RecordState = Extract<ResolvedRecordState, 'Signed' | 'Superseded'>;

export type SortDirection = 'asc' | 'desc';

export type CaseRegisterSortKey =
  | 'case'
  | 'condition'
  | 'siteArea'
  | 'owner'
  | 'controlStage'
  | 'currentScan'
  | 'latestScan'
  | 'latestSignedScan'
  | 'newActivity'
  | 'nextAction'
  | 'due'
  | 'updated';

export type ScanRegisterSortKey =
  | 'scan'
  | 'definition'
  | 'trigger'
  | 'siteZone'
  | 'window'
  | 'coverage'
  | 'result'
  | 'workflowState'
  | 'relatedCase'
  | 'completed'
  | 'action';

export type RecordRegisterSortKey =
  | 'record'
  | 'safetyCase'
  | 'revision'
  | 'scope'
  | 'signedBy'
  | 'signedDate'
  | 'newActivity'
  | 'package'
  | 'action';

export type RegisterCaseSource = {
  key: string;
  caseId: string | null;
  title: string;
  condition: string;
  riskType?: string | null;
  hazardClassKey?: HazardClassKey | string | null;
  siteArea: string;
  cameraLabel?: string | null;
  owner: string | null;
  ownerIdentity?: string | null;
  workflowStatus?: string | null;
  workflowStatusLabel?: string | null;
  correctiveActionStatus?: string | null;
  correctiveActionStatusLabel?: string | null;
  safetyCaseStatus?: string | null;
  safetyCaseStatusLabel?: string | null;
  customerControlStage?: string | null;
  currentScanLabel?: string | null;
  currentScanWorkflowState?: ScanWorkflowState | null;
  currentScanResult?: string | null;
  currentOccurrenceCount?: number | null;
  currentUsableHours?: number | null;
  currentRatePerUsableHour?: number | null;
  currentScanLink?: RegisterCaseScanLinkSource | null;
  latestScanLabel?: string | null;
  latestScanWorkflowState?: ScanWorkflowState | null;
  latestSignedScanLabel?: string | null;
  latestSignedScanLink?: RegisterCaseScanLinkSource | null;
  currentScanEmptyReason?: string | null;
  workflowId?: string | null;
  scanCount?: number | null;
  hasNewActivity: boolean;
  newActivityCount?: number | null;
  recommendedNextStep?: string | null;
  dueAt?: string | null;
  updatedAt?: string | null;
};

export type RegisterCaseScanLinkSource = {
  id: string;
  reportId: string | null;
  logicalScanKey?: string | null;
  caseKey: string;
  caseId: string | null;
  scanName: string;
  definition?: string | null;
  trigger?: 'Scheduled' | 'Manual' | 'Follow-up' | null;
  siteZone: string;
  windowStart?: string | null;
  windowEnd?: string | null;
  workflowState: ScanWorkflowState;
  completedAt?: string | null;
};

export type RegisterCaseScanHistoryLike = {
  id: string;
  reportId: string | null;
  logicalScanKey: string;
  scanName: string;
  statusKind: string;
  triggerType: string | null;
  scopeStart: string | null;
  scopeEnd: string | null;
  signedAt: string | null;
  actionRow: {
    report_status?: string | null;
    signoff_status?: string | null;
    impact_measurement_status?: string | null;
    recommended_next_step?: string | null;
    customer_scan_workflow_state?: string | null;
    blockers?: Array<{ code?: string | null }>;
  };
};

export function buildRegisterCaseScanLinkSource(args: {
  caseKey: string;
  caseId: string | null;
  scan: RegisterCaseScanHistoryLike | null;
  definition?: string | null;
  siteZone: string;
}): RegisterCaseScanLinkSource | null {
  const { scan } = args;
  if (!scan) return null;
  const workflowState = scan.statusKind === 'signed'
    ? 'Signed'
    : mapScanWorkflowState({
        reportStatus: scan.actionRow.report_status || 'draft',
        signoffStatus: scan.actionRow.signoff_status,
        historyStatus: scan.statusKind,
        impactMeasurementStatus: scan.actionRow.impact_measurement_status,
        recommendedNextStep: scan.actionRow.recommended_next_step,
        blockerCodes: (scan.actionRow.blockers || []).map((blocker) => String(blocker.code || '')),
        customerScanWorkflowState: scan.actionRow.customer_scan_workflow_state,
      });
  return {
    id: `${args.caseKey}:${scan.id}`, reportId: scan.reportId, logicalScanKey: scan.logicalScanKey,
    caseKey: args.caseKey, caseId: args.caseId, scanName: scan.scanName,
    definition: args.definition || null, trigger: mapScanRegisterTrigger(scan.triggerType), siteZone: args.siteZone,
    windowStart: scan.scopeStart, windowEnd: scan.scopeEnd, workflowState, completedAt: scan.signedAt || scan.scopeEnd || null,
  };
}

export type RegisterScanSource = {
  id: string;
  reportId: string | null;
  logicalScanKey?: string | null;
  caseKey: string;
  caseId: string | null;
  caseTitle: string;
  scanName: string;
  definition?: string | null;
  /** Only set when a known trigger signal exists — never invent Manual/Scheduled. */
  trigger?: 'Scheduled' | 'Manual' | 'Follow-up' | null;
  siteZone: string;
  windowStart?: string | null;
  windowEnd?: string | null;
  reportStatus?: string | null;
  signoffStatus?: string | null;
  historyStatus?: string | null;
  impactMeasurementStatus?: string | null;
  recommendedNextStep?: string | null;
  blockerCodes?: string[];
  customerScanWorkflowState?: string | null;
  customerScanResult?: string | null;
  occurrenceCount?: number | null;
  measuredOccurrenceCount?: number | null;
  reviewedOccurrenceCount?: number | null;
  confirmedOccurrenceCount?: number | null;
  usableHours?: number | null;
  coverageState?: string | null;
  verificationState?: string | null;
  ratePerUsableHour?: number | null;
  completedAt?: string | null;
};

export type RegisterRecordSource = {
  id: string;
  reportId: string | null;
  caseKey: string;
  caseId: string | null;
  caseTitle: string;
  recordName: string;
  revision?: number | string | null;
  revisions?: Array<{ revision?: number | null }> | null;
  scopeStart?: string | null;
  scopeEnd?: string | null;
  signedBy?: string | null;
  signedAt?: string | null;
  hasNewActivity: boolean;
  packageAvailable: boolean;
  /** Newest signed revision for the case → Signed; older signed → Superseded. */
  isLatestSigned: boolean;
};

export type CaseRegisterRow = {
  id: string;
  caseId: string | null;
  caseLabel: string;
  condition: string;
  hazardClassKey?: HazardClassKey | null;
  siteArea: string;
  owner: string;
  /** Stable login/account identity used by My open work. */
  ownerIdentity?: string | null;
  controlStage: ControlStage;
  managementState: BuyerCaseState;
  statusLabel: string;
  currentScan: string;
  currentMeasurement: string;
  currentScanEmptyReason?: string | null;
  workflowId?: string | null;
  latestScan: string;
  latestSignedScan: string;
  currentScanLink?: CaseRegisterScanLink | null;
  latestSignedScanLink?: CaseRegisterScanLink | null;
  scanCountLabel: string;
  newActivity: string;
  controlLabel: string;
  nextAction: string;
  /** Owner is still unresolved even when Next Step is review of new activity. */
  ownerUnresolved?: boolean;
  dueAt: string | null;
  updatedAt: string | null;
  actionLabel: string;
  actionKind: 'open_case' | 'start_monitoring';
  /** Short Confirmed vs Monitoring helper when Start monitoring is offered. */
  actionHelper?: string | null;
};

export type CaseRegisterScanLink = {
  id: string;
  reportId: string | null;
  logicalScanKey: string;
  caseKey: string;
  caseId: string | null;
  scanLabel: string;
  definition: string;
  trigger: string;
  siteZone: string;
  windowLabel: string;
  workflowState: ScanWorkflowState;
  completedAt: string | null;
  actionLabel: string;
  actionKind: 'open_signed_scan' | 'open_draft_scan' | 'open_case';
};

export type ScanRegisterRow = {
  id: string;
  reportId: string | null;
  logicalScanKey: string;
  caseKey: string;
  caseId: string | null;
  scanLabel: string;
  definition: string;
  trigger: string;
  siteZone: string;
  windowLabel: string;
  coverage: string;
  result: string;
  resultSecondary?: string | null;
  resultTertiary?: string | null;
  verificationLabel?: string;
  needsReview?: boolean;
  workflowState: ScanWorkflowState;
  relatedCase: string;
  completedAt: string | null;
  actionLabel: string;
  actionAriaLabel?: string;
  actionKind: 'open_signed_scan' | 'open_draft_scan' | 'open_case';
};

function actionForScanWorkflowState(
  workflowState: ScanWorkflowState,
  reportId: string | null,
): Pick<CaseRegisterScanLink | ScanRegisterRow, 'actionLabel' | 'actionKind'> {
  if (workflowState === 'Signed' && reportId) {
    return { actionKind: 'open_signed_scan', actionLabel: VIEW_SAFETY_SCAN_LABEL };
  }
  if (reportId) {
    return {
      actionKind: 'open_draft_scan',
      actionLabel: workflowState === 'Awaiting sign-off'
        ? REVIEW_AND_SIGN_LABEL
        : workflowState === 'Failed'
          ? 'Review Scan issue'
          : OPEN_SCAN_LABEL,
    };
  }
  return { actionKind: 'open_case', actionLabel: OPEN_SAFETY_CASE_LABEL };
}

function buildCaseScanLink(source?: RegisterCaseScanLinkSource | null): CaseRegisterScanLink | null {
  if (!source) return null;
  const reportId = source.reportId || null;
  const workflowState = source.workflowState;
  const action = actionForScanWorkflowState(workflowState, reportId);
  return {
    id: source.id,
    reportId,
    logicalScanKey: String(source.logicalScanKey || reportId || source.id).trim(),
    caseKey: source.caseKey,
    caseId: source.caseId,
    scanLabel: source.scanName,
    definition: buyerScanDefinitionDisplayName(source.definition) || '—',
    trigger: source.trigger || '—',
    siteZone: source.siteZone || '—',
    windowLabel: formatWindow(source.windowStart, source.windowEnd),
    workflowState,
    completedAt: source.completedAt || null,
    ...action,
  };
}

export type RecordRegisterRow = {
  id: string;
  reportId: string | null;
  caseKey: string;
  caseId: string | null;
  recordLabel: string;
  safetyCase: string;
  revision?: number | null;
  revisionLabel: string;
  scopeLabel: string;
  signedBy: string;
  signedAt: string | null;
  recordState: RecordState;
  newActivity: string;
  packageLabel: string;
  packageAvailable: boolean;
  actionLabel: string;
  actionKind: 'view_signed_record' | 'open_case';
};

export function buildSignedRecordPath(
  row: Pick<RecordRegisterRow, 'reportId' | 'revision'>,
  searchParams: URLSearchParams,
): string | null {
  if (!row.reportId || row.revision == null) return null;
  const query = searchParams.toString();
  const path = `/acceptance/reports/records/${encodeURIComponent(row.reportId)}/revisions/${row.revision}`;
  return query ? `${path}?${query}` : path;
}

function normalize(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function dateTimestamp(value?: string | null): number {
  const ms = new Date(String(value || '')).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function formatShortDate(value?: string | null): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(parsed);
}

function formatWindow(start?: string | null, end?: string | null): string {
  const startLabel = formatShortDate(start);
  const endLabel = formatShortDate(end);
  if (startLabel && endLabel) return `${startLabel} – ${endLabel}`;
  if (endLabel) return `Through ${endLabel}`;
  if (startLabel) return `From ${startLabel}`;
  return '—';
}

function normalizeHazardClassLabel(value: unknown): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[_/|·–—-]+/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const HAZARD_CLASS_LABEL_ALIASES: Record<string, HazardClassKey> = {
  'access control restricted zone entry': 'restricted_zone_intrusion',
  'restricted zone': 'restricted_zone_intrusion',
  'restricted zone entry': 'restricted_zone_intrusion',
  'restricted zone intrusion': 'restricted_zone_intrusion',
  'zone intrusion': 'restricted_zone_intrusion',
  'zone violation': 'restricted_zone_intrusion',
  'restricted zone activity': 'restricted_zone_intrusion',
  'vehicle pedestrian interaction': 'mobile_equipment_separation',
  'pedestrian mobile equipment interaction': 'mobile_equipment_separation',
  'mobile equipment interaction': 'mobile_equipment_separation',
  'forklift pedestrian': 'mobile_equipment_separation',
  'forklift pedestrian interaction': 'mobile_equipment_separation',
  'ppe compliance': 'ppe_compliance',
  'ppe visibility': 'ppe_compliance',
  'worker down': 'worker_down_response',
  'worker down delayed response': 'worker_down_response',
};

function resolveHazardClassKeyLabel(value: unknown): HazardClassKey | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const directKey = raw.toLowerCase().replace(/[\s-]+/g, '_');
  if (isHazardClassKey(directKey)) return directKey;

  const normalized = normalizeHazardClassLabel(raw);
  if (isHazardClassKey(normalized.replace(/\s+/g, '_'))) {
    return normalized.replace(/\s+/g, '_') as HazardClassKey;
  }

  const option = V1_HAZARD_CLASS_OPTIONS.find((candidate) => (
    normalizeHazardClassLabel(candidate.displayName) === normalized
    || normalizeHazardClassLabel(candidate.executiveRiskLabel) === normalized
  ));
  if (option) return option.key;

  return HAZARD_CLASS_LABEL_ALIASES[normalized] || null;
}

export function nextActionForCase(source: RegisterCaseSource, controlStage: ControlStage): string {
  const scanCount = typeof source.scanCount === 'number' && Number.isFinite(source.scanCount)
    ? Math.max(0, Math.floor(source.scanCount))
    : null;
  if (source.hasNewActivity) return REVIEW_SAFETY_SCAN_LABEL;
  if (!hasAssignedOwner(source) && controlStage !== 'Closed') return 'Assign owner';
  if (source.currentScanWorkflowState === 'Awaiting sign-off' || source.latestScanWorkflowState === 'Awaiting sign-off') {
    return REVIEW_SAFETY_SCAN_LABEL;
  }
  if (source.currentScanWorkflowState === 'Insufficient coverage' || source.latestScanWorkflowState === 'Insufficient coverage') {
    return 'Resolve coverage issue';
  }
  if (hasSignedBaselineWithoutControl(source)) return 'Record control';
  const step = normalize(source.recommendedNextStep);
  if (step === 'complete_impact_coverage' || step === 'resolve_coverage_issue') return 'Resolve coverage issue';
  if (controlStage === 'Closed') return 'No action required';
  if (controlStage === 'Follow-up measurement scheduled') return 'Start follow-up measurement';
  if (scanCount === 0 || controlStage === 'Confirmed' || controlStage === 'Monitoring') return 'No action required';
  return OPEN_SAFETY_CASE_LABEL;
}

function resolveCaseHazardClassKey(source: RegisterCaseSource): HazardClassKey | null {
  const explicitKey = resolveHazardClassKeyLabel(source.hazardClassKey);
  if (explicitKey) return explicitKey;
  return resolveIncidentHazardClassKey({
    policy_type: source.riskType,
    incident_type: source.riskType,
  });
}

function hasAssignedOwner(source: RegisterCaseSource): boolean {
  const owner = normalize(source.owner);
  return Boolean(owner) && !['unassigned', 'not assigned', 'none'].includes(owner);
}

function hasSignedBaselineWithoutControl(source: RegisterCaseSource): boolean {
  const signed = normalize(source.latestSignedScanLabel || source.latestScanLabel || source.latestScanWorkflowState);
  const currentScan = normalize(source.currentScanLabel || source.currentScanWorkflowState);
  const corrective = normalize(source.correctiveActionStatus || source.correctiveActionStatusLabel);
  return signed === 'signed'
    && (!currentScan || currentScan === '—' || currentScan === 'no current scan')
    && (!corrective || corrective === 'not_started' || corrective === 'not started');
}

function deterministicControlStage(source: RegisterCaseSource, mapped: ControlStage): ControlStage {
  // Confirmed is customer-visible management intake: admitted Case, but not yet Monitoring.
  if (source.hasNewActivity && mapped === 'Monitoring') return 'Reopened';
  if (!hasAssignedOwner(source) && mapped === 'Monitoring') return 'Confirmed';
  if (hasSignedBaselineWithoutControl(source) && mapped === 'Monitoring') return 'Confirmed';
  return mapped;
}

export function buildCaseRegisterRows(sources: RegisterCaseSource[]): CaseRegisterRow[] {
  return sources.map((source) => {
    const mappedControlStage = mapControlStage({
      workflowStatus: source.workflowStatus,
      workflowStatusLabel: source.workflowStatusLabel,
      correctiveActionStatus: source.correctiveActionStatus,
      correctiveActionStatusLabel: source.correctiveActionStatusLabel,
      safetyCaseStatus: source.safetyCaseStatus,
      safetyCaseStatusLabel: source.safetyCaseStatusLabel,
      customerControlStage: source.customerControlStage,
    });
    const controlStage = deterministicControlStage(source, mappedControlStage);
    const count = source.newActivityCount;
    const scanCount = typeof source.scanCount === 'number' && Number.isFinite(source.scanCount)
      ? Math.max(0, Math.floor(source.scanCount))
      : null;
    const managementState = mapBuyerCaseState({
      controlStage,
      hasNewActivity: source.hasNewActivity,
      scanCount,
    });
    return {
      id: source.key,
      caseId: source.caseId,
      caseLabel: source.title,
      condition: buyerConditionDisplayName(source.condition || source.title, source.riskType),
      hazardClassKey: resolveCaseHazardClassKey(source),
      siteArea: formatCameraLevelScope({
        siteArea: source.siteArea,
        cameraLabel: source.cameraLabel,
      }) || '—',
      owner: buyerOwnerDisplayName(source.owner),
      ownerIdentity: String(source.ownerIdentity || '').trim() || null,
      controlStage,
      managementState,
      statusLabel: mapBuyerCaseStatusLabel(managementState),
      currentScan: source.currentScanLabel
        || (source.currentScanWorkflowState ? source.currentScanWorkflowState : null)
        || NO_CURRENT_SCAN_LABEL,
      currentMeasurement: formatPersistedScanResult({
        customerScanResult: source.currentScanResult,
        occurrenceCount: source.currentOccurrenceCount,
        usableHours: source.currentUsableHours,
        ratePerUsableHour: source.currentRatePerUsableHour,
        workflowState: source.currentScanWorkflowState,
        coverageInsufficient: source.currentScanWorkflowState === 'Insufficient coverage',
      }),
      currentScanEmptyReason: source.currentScanEmptyReason || null,
      workflowId: String(source.workflowId || '').trim() || null,
      latestScan: source.latestScanLabel
        || (source.latestScanWorkflowState ? source.latestScanWorkflowState : '—'),
      latestSignedScan: source.latestSignedScanLabel
        || (source.latestScanWorkflowState === 'Signed' ? (source.latestScanLabel || 'Signed') : '—'),
      currentScanLink: buildCaseScanLink(source.currentScanLink),
      latestSignedScanLink: buildCaseScanLink(source.latestSignedScanLink),
      scanCountLabel: scanCount == null ? '—' : String(scanCount),
      newActivity: source.hasNewActivity
        ? (typeof count === 'number' && Number.isFinite(count)
          ? `${count} new`
          : 'Yes')
        : 'No',
      controlLabel: String(source.correctiveActionStatusLabel || source.correctiveActionStatus || '').trim()
        || 'Not recorded',
      nextAction: nextActionForCase(source, controlStage),
      ownerUnresolved: !hasAssignedOwner(source),
      dueAt: source.dueAt || null,
      updatedAt: source.updatedAt || null,
      actionLabel: OPEN_SAFETY_CASE_LABEL,
      actionKind: 'open_case',
      actionHelper: null,
    };
  });
}

export function caseRegisterIssueDisplay(row: Pick<CaseRegisterRow, 'caseLabel' | 'condition'>): {
  issue: string;
  canonicalTitle: string | null;
} {
  const issue = buyerConditionDisplayName(row.condition || row.caseLabel);
  const canonical = String(row.caseLabel || '').trim();
  const mappedCanonical = canonical ? buyerConditionDisplayName(canonical) : '';
  if (issue && issue !== '—') {
    return {
      issue,
      canonicalTitle: mappedCanonical && mappedCanonical.toLowerCase() !== issue.toLowerCase()
        ? canonical
        : null,
    };
  }
  return { issue: canonical || '—', canonicalTitle: null };
}

export function buildScanRegisterRows(sources: RegisterScanSource[]): ScanRegisterRow[] {
  return sources.map((source) => {
    const workflowState = mapScanWorkflowState({
      reportStatus: source.reportStatus,
      signoffStatus: source.signoffStatus,
      historyStatus: source.historyStatus,
      impactMeasurementStatus: source.impactMeasurementStatus,
      recommendedNextStep: source.recommendedNextStep,
      blockerCodes: source.blockerCodes,
      customerScanWorkflowState: source.customerScanWorkflowState,
    });
    const hierarchy = formatBuyerScanResultHierarchy({
      measuredOccurrenceCount: source.measuredOccurrenceCount,
      reviewedOccurrenceCount: source.reviewedOccurrenceCount,
      confirmedOccurrenceCount: source.confirmedOccurrenceCount,
      usableHours: source.usableHours,
      coverageState: source.coverageState,
      verificationState: source.verificationState,
    });
    const verificationLabel = hierarchy.verificationLabel;
    const needsReview = hierarchy.needsReview || workflowState === 'Review required' || workflowState === 'Failed';
    const action = actionForScanWorkflowState(workflowState, source.reportId);
    const buyerAction = source.reportId
      ? buyerScanActionPresentation({
          reportId: source.reportId,
          needsReview,
          workflowState,
        })
      : { actionLabel: action.actionLabel, actionAriaLabel: action.actionLabel };
    const relatedCase = managedCaseLabel(
      buyerConditionDisplayName(source.caseTitle, source.definition),
      source.caseId,
    );
    const definition = buyerScanDefinitionDisplayName(source.definition || source.caseTitle) || '—';
    return {
      id: source.id,
      reportId: source.reportId,
      logicalScanKey: String(source.logicalScanKey || source.reportId || source.id).trim(),
      caseKey: source.caseKey,
      caseId: source.caseId,
      scanLabel: source.scanName,
      definition,
      trigger: source.trigger || '—',
      siteZone: dedupeBuyerLocationLabel(source.siteZone) || '—',
      windowLabel: formatWindow(source.windowStart, source.windowEnd),
      coverage: hierarchy.coverageLabel || hierarchy.tertiary || '—',
      result: hierarchy.primary,
      resultSecondary: hierarchy.secondary,
      resultTertiary: hierarchy.tertiary,
      verificationLabel,
      needsReview,
      workflowState,
      relatedCase,
      completedAt: source.completedAt || null,
      actionLabel: buyerAction.actionLabel,
      actionAriaLabel: buyerAction.actionAriaLabel,
      actionKind: action.actionKind,
    };
  });
}

/** Records register: signed + superseded only (draft / awaiting sign-off excluded). */
export function buildRecordRegisterRows(sources: RegisterRecordSource[]): RecordRegisterRow[] {
  return sources.map((source) => {
    const recordState: RecordState = source.isLatestSigned ? 'Signed' : 'Superseded';
    const revision = coerceRegisterRevision(source);
    return {
      id: source.id,
      reportId: source.reportId,
      caseKey: source.caseKey,
      caseId: source.caseId,
      recordLabel: source.recordName,
      safetyCase: source.caseTitle || '—',
      revision,
      revisionLabel: revision != null ? `Rev ${revision}` : '—',
      scopeLabel: formatWindow(source.scopeStart, source.scopeEnd),
      signedBy: String(source.signedBy || '').trim() || '—',
      signedAt: source.signedAt || null,
      recordState,
      newActivity: source.hasNewActivity ? 'Yes' : 'No',
      // Package availability is never presented as approval.
      packageLabel: source.packageAvailable ? 'Download available' : 'Not available',
      packageAvailable: Boolean(source.packageAvailable),
      actionLabel: VIEW_RECORD_LABEL,
      actionKind: 'view_signed_record',
    };
  });
}

function compareNullableString(left: string | null | undefined, right: string | null | undefined, direction: SortDirection): number {
  const a = String(left || '').trim();
  const b = String(right || '').trim();
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  const cmp = a.localeCompare(b, undefined, { sensitivity: 'base' });
  return direction === 'asc' ? cmp : -cmp;
}

function compareNullableDate(left: string | null | undefined, right: string | null | undefined, direction: SortDirection): number {
  const a = dateTimestamp(left);
  const b = dateTimestamp(right);
  if (a === b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return direction === 'asc' ? a - b : b - a;
}

export function sortCaseRegisterRows(
  rows: CaseRegisterRow[],
  sortKey: CaseRegisterSortKey,
  direction: SortDirection,
): CaseRegisterRow[] {
  const sorted = [...rows];
  sorted.sort((left, right) => {
    switch (sortKey) {
      case 'updated':
      case 'due':
        return compareNullableDate(
          sortKey === 'due' ? left.dueAt : left.updatedAt,
          sortKey === 'due' ? right.dueAt : right.updatedAt,
          direction,
        );
      case 'controlStage':
        return compareNullableString(left.controlStage, right.controlStage, direction);
      case 'newActivity':
        return compareNullableString(left.newActivity, right.newActivity, direction);
      case 'case':
        return compareNullableString(left.caseLabel, right.caseLabel, direction);
      case 'condition':
        return compareNullableString(left.condition, right.condition, direction);
      case 'siteArea':
        return compareNullableString(left.siteArea, right.siteArea, direction);
      case 'owner':
        return compareNullableString(left.owner, right.owner, direction);
      case 'latestScan':
        return compareNullableString(left.latestScan, right.latestScan, direction);
      case 'currentScan':
        return compareNullableString(left.currentScan, right.currentScan, direction);
      case 'latestSignedScan':
        return compareNullableString(left.latestSignedScan, right.latestSignedScan, direction);
      case 'nextAction':
        return compareNullableString(left.nextAction, right.nextAction, direction);
      default:
        return 0;
    }
  });
  return sorted;
}

export function sortScanRegisterRows(
  rows: ScanRegisterRow[],
  sortKey: ScanRegisterSortKey,
  direction: SortDirection,
): ScanRegisterRow[] {
  const sorted = [...rows];
  sorted.sort((left, right) => {
    switch (sortKey) {
      case 'completed':
        return compareNullableDate(left.completedAt, right.completedAt, direction);
      case 'scan':
        return compareNullableString(left.scanLabel, right.scanLabel, direction);
      case 'definition':
        return compareNullableString(left.definition, right.definition, direction);
      case 'trigger':
        return compareNullableString(left.trigger, right.trigger, direction);
      case 'siteZone':
        return compareNullableString(left.siteZone, right.siteZone, direction);
      case 'window':
        return compareNullableString(left.windowLabel, right.windowLabel, direction);
      case 'coverage':
        return compareNullableString(left.coverage, right.coverage, direction);
      case 'result':
        return compareNullableString(left.result, right.result, direction);
      case 'workflowState':
        return compareNullableString(left.workflowState, right.workflowState, direction);
      case 'relatedCase':
        return compareNullableString(left.relatedCase, right.relatedCase, direction);
      case 'action':
        return compareNullableString(left.actionLabel, right.actionLabel, direction);
      default:
        return 0;
    }
  });
  return sorted;
}

const SHOW_SUPERSEDED_RECORDS_PARAM = 'show_superseded';

export function showSupersededRecordsParam(): string {
  return SHOW_SUPERSEDED_RECORDS_PARAM;
}

/** URL toggle: show_superseded=1 (also accepts true/yes). Default hides superseded rows. */
export function resolveShowSupersededRecords(raw: string | null | undefined): boolean {
  const normalized = String(raw || '').trim().toLowerCase();
  return normalized === '1' || normalized === 'true' || normalized === 'yes';
}

/** Hide superseded revision history unless the viewer explicitly opts in. */
export function filterRecordsBySupersededVisibility(
  rows: RecordRegisterRow[],
  showSuperseded: boolean,
): RecordRegisterRow[] {
  if (showSuperseded) return rows;
  return rows.filter((row) => row.recordState !== 'Superseded');
}

function recordStateRank(state: RecordState): number {
  return state === 'Signed' ? 0 : 1;
}

function hasRecordNewActivity(row: RecordRegisterRow): boolean {
  const label = String(row.newActivity || '').trim().toLowerCase();
  return Boolean(label) && label !== 'no' && label !== '—' && label !== '-';
}

/**
 * When New activity saved view is active, keep current signed rows ahead of superseded
 * history and surface new-activity rows first within each state band.
 */
export function orderRecordRegisterRowsForSavedView(
  rows: RecordRegisterRow[],
  savedViewId: string | null | undefined,
): RecordRegisterRow[] {
  if (savedViewId !== 'new_activity') return rows;
  return [...rows].sort((left, right) => {
    const stateCmp = recordStateRank(left.recordState) - recordStateRank(right.recordState);
    if (stateCmp !== 0) return stateCmp;
    const activityCmp = Number(hasRecordNewActivity(right)) - Number(hasRecordNewActivity(left));
    if (activityCmp !== 0) return activityCmp;
    return compareNullableDate(left.signedAt, right.signedAt, 'desc');
  });
}

export function sortRecordRegisterRows(
  rows: RecordRegisterRow[],
  sortKey: RecordRegisterSortKey,
  direction: SortDirection,
): RecordRegisterRow[] {
  const sorted = [...rows];
  sorted.sort((left, right) => {
    switch (sortKey) {
      case 'signedDate':
        return compareNullableDate(left.signedAt, right.signedAt, direction);
      case 'record':
        return compareNullableString(left.recordLabel, right.recordLabel, direction);
      case 'safetyCase':
        return compareNullableString(left.safetyCase, right.safetyCase, direction);
      case 'revision':
        return compareNullableString(left.revisionLabel, right.revisionLabel, direction);
      case 'scope':
        return compareNullableString(left.scopeLabel, right.scopeLabel, direction);
      case 'signedBy':
        return compareNullableString(left.signedBy, right.signedBy, direction);
      case 'newActivity':
        return compareNullableString(left.newActivity, right.newActivity, direction);
      case 'package':
        return compareNullableString(left.packageLabel, right.packageLabel, direction);
      case 'action':
        return compareNullableString(left.actionLabel, right.actionLabel, direction);
      default:
        return 0;
    }
  });
  return sorted;
}

export function paginateRegisterRows<T>(rows: T[], visibleCount: number): {
  visible: T[];
  hasMore: boolean;
  total: number;
} {
  const capped = Math.max(0, Math.floor(visibleCount));
  return {
    visible: rows.slice(0, capped),
    hasMore: rows.length > capped,
    total: rows.length,
  };
}

/** Whether the dashboard fetch can request a larger capped page (server limit ≤ 100). */
export function canIncreaseDashboardFetch(args: {
  fetchedCount: number;
  fetchLimit: number;
  maxLimit?: number;
}): boolean {
  const maxLimit = args.maxLimit ?? DASHBOARD_FETCH_MAX;
  return args.fetchedCount >= args.fetchLimit && args.fetchLimit < maxLimit;
}

export function nextDashboardFetchLimit(current: number, pageSize = DASHBOARD_FETCH_PAGE): number {
  return Math.min(DASHBOARD_FETCH_MAX, Math.max(pageSize, current + pageSize));
}
